// Krapf charter intake — the seam between an Outlook thread and a Movorra Studio (ECAP) flow session.
//
// One email thread <-> one flow session. Each inbound email is one turn. The flow reports what it
// still needs; THIS module decides that it all goes into one email as a numbered list. That split is
// the whole design: the engine owns what is missing, the composer owns how it is asked.
//
// Why we compose instead of sending the engine's own `reply`: SessionEngine asks exactly ONE field
// per turn. In chat a follow-up costs seconds; over email it costs a day, so a five-gap inquiry would
// take five days. This is a deliberate, approved departure from the "one question per turn" standing
// rule in CLAUDE.md, which was written for live chat.
//
// Rules this module must not break (each is a verified engine behaviour, see
// movorra-flows/templates/krapf/README.md):
//   • Render missingFields in the ORDER RETURNED. It is built by walking fields in declaration order
//     and askGroup anchors the extractor to that same order; re-sorting for readability makes a terse
//     reply land in the wrong field.
//   • TurnResult.missingFields is the ONLY authoritative source. GET /runtime/sessions/{id} computes
//     its list from required() alone, ignoring requiredIf / excused / suppressed / derived.
//   • Terminate on status === 'COMPLETE', never on missingFields.length === 0 — they are not the
//     same condition.
//   • Never leak a review flag into a customer email. Two channels, never merged.
//   • A field is "optional" only when isRequiredNow() says so. The static `required` flag misses
//     every requiredIf field — the return leg and the PO number.
import { startSession, sendMessage, submitComponentResult } from './ecapApi';
import { FIELD_BY_KEY, INTAKE_FIELDS } from './krapfIntakeFields';
import { messageToTurnText, capTurnText, replySubject } from './emailText';

// ---- thread <-> session mapping ------------------------------------------------------------
// Demo-grade persistence: keyed by the Graph conversationId, stored per browser. The ECAP sessions
// themselves are server-side and durable; only this LINK to them is local, so clearing site data
// orphans in-flight threads (recoverable only by starting the thread over). A production build keeps
// this mapping server-side — called out in the plan rather than hidden here.
const MAP_KEY = 'krapf.threadSessions.v1';

function readMap() {
  try { return JSON.parse(window.localStorage.getItem(MAP_KEY) || '{}'); } catch (e) { return {}; }
}

function writeMap(m) {
  try { window.localStorage.setItem(MAP_KEY, JSON.stringify(m)); } catch (e) { /* private mode */ }
}

export function sessionForThread(conversationId) {
  return readMap()[conversationId] || null;
}

export function watchedThreadIds() {
  return Object.keys(readMap());
}

export function isWatched(conversationId) {
  return !!sessionForThread(conversationId);
}

function rememberThread(conversationId, sessionId, lastMessageId) {
  const m = readMap();
  m[conversationId] = { sessionId, lastMessageId, startedAt: Date.now() };
  writeMap(m);
}

export function noteLastMessage(conversationId, messageId) {
  const m = readMap();
  if (m[conversationId]) { m[conversationId].lastMessageId = messageId; writeMap(m); }
}

export function forgetThread(conversationId) {
  const m = readMap();
  delete m[conversationId];
  writeMap(m);
}

/**
 * The last TurnResult for a thread, plus the unsent draft and whether we are waiting on the customer.
 *
 * Why this is CACHED rather than re-fetched: the only other way to rebuild the panel is
 * GET /runtime/sessions/{id}, whose ResumeInfo computes its missing list from required() alone,
 * ignoring requiredIf / excused / suppressed / derived. Composing from that would re-ask for a PO
 * number nobody owes and re-ask a field the engine already excused. TurnResult.missingFields is the
 * only authoritative source, so the authoritative thing is what we keep.
 *
 * Stored on the SAME record as the session link, so a mailbox reset clears both together.
 */
export function saveThreadState(conversationId, { turn, draft, awaitingCustomer } = {}) {
  const m = readMap();
  // Unwatched threads have no record: a thread nobody marked as an inquiry must not gain one here.
  if (!m[conversationId]) return;
  if (turn !== undefined) m[conversationId].turn = turn;
  if (draft !== undefined) m[conversationId].draft = draft;
  if (awaitingCustomer !== undefined) m[conversationId].awaitingCustomer = !!awaitingCustomer;
  writeMap(m);
}

/** Everything needed to re-open a thread exactly as it was left. Null when the thread is not watched. */
export function threadState(conversationId) {
  const rec = readMap()[conversationId];
  if (!rec) return null;
  return {
    sessionId: rec.sessionId,
    turn: rec.turn || null,
    draft: rec.draft || '',
    awaitingCustomer: !!rec.awaitingCustomer,
  };
}

// ---- running turns -------------------------------------------------------------------------

/**
 * Mark a thread as a trip inquiry: start a session and feed the opening email as turn 1.
 * The runtime returns a greeting on start; we discard it — the inquiry is the opening move, which is
 * what "no opening message" means for this flow.
 */
export async function beginIntake(flowKey, message) {
  const started = await startSession(flowKey);
  const text = capTurnText(messageToTurnText(message, { withHeaders: true }));
  const turn = await sendMessage(started.sessionId, text);
  rememberThread(message.conversationId, started.sessionId, message.id);
  return { sessionId: started.sessionId, turn };
}

/** Feed a customer reply in as the next turn. Body-only: the header block would be stale noise. */
export async function continueIntake(sessionId, message) {
  const text = capTurnText(messageToTurnText(message, { withHeaders: false }));
  const turn = await sendMessage(sessionId, text);
  noteLastMessage(message.conversationId, message.id);
  return turn;
}

/**
 * The operator's trip-kind call, written straight onto the session.
 *
 * component-result injects the value and resumes the engine with no user message, which is the
 * RESUME path: no LLM call, no email, no turn spent. requiredIf then does the rest — asserting
 * "round-trip" turns the suppressed return fields into real gaps and the composed reply grows the
 * questions; flipping back retracts them.
 *
 * `kind` must be one of the flow's own options (FIELD_BY_KEY.returnJourney.options) and nothing
 * else: the endpoint does no key or value filtering whatsoever, so free input must never reach it.
 */
export async function setTripKind(sessionId, kind) {
  const allowed = (FIELD_BY_KEY.returnJourney && FIELD_BY_KEY.returnJourney.options) || [];
  if (!allowed.includes(kind)) throw new Error(`Not a trip kind this flow knows: ${kind}`);
  return submitComponentResult(sessionId, { returnJourney: kind });
}

// ---- reading a turn ------------------------------------------------------------------------

/**
 * Collected values, with the engine's internal bookkeeping removed. TurnResult.resolvedValues is
 * `new LinkedHashMap<>(session.values())` — the whole map, including the reserved `__askcount:`,
 * `__excused:` and `__pending:` entries. Those are not customer data and must never reach a screen.
 */
export function cleanValues(turn) {
  const out = {};
  Object.entries((turn && turn.resolvedValues) || {}).forEach(([k, v]) => {
    if (!k.startsWith('__')) out[k] = v;
  });
  return out;
}

/** The "Still needed to quote" list — in the order the engine returned it. Never re-sorted. */
export function missingItems(turn) {
  return ((turn && turn.missingFields) || []).map((key) => ({
    key,
    label: (FIELD_BY_KEY[key] && FIELD_BY_KEY[key].label) || key,
    prompt: (FIELD_BY_KEY[key] && FIELD_BY_KEY[key].prompt) || `Could you tell us ${key}?`,
  }));
}

/**
 * Is this field required RIGHT NOW? Mirrors the engine's own requiredNow/isSuppressed
 * (SessionEngine.java): statically required, or carrying a requiredIf whose condition is satisfied.
 *
 * This is the one piece of engine logic the client genuinely has to re-implement. A field whose
 * requiredIf is FALSE is SUPPRESSED server-side — never asked, never offered, absent from
 * missingFields — but it is still a perfectly ordinary entry in INTAKE_FIELDS, so anything here
 * that reasons about "optional" from the static `required` flag alone gets it wrong: it would offer
 * a one-way customer a return date, and route a round trip's return-date error to the reviewer
 * instead of to the customer who needs to answer it.
 *
 * Only the operators the engine supports (BranchSelector.matchOne): EQUALS, NOT_EQUALS, EXISTS,
 * CONTAINS, defaulting to EQUALS, with one condition per field.
 */
export function isRequiredNow(field, values) {
  if (!field) return false;
  if (field.required) return true;
  const c = field.requiredIf;
  if (!c) return false;
  const v = (values || {})[c.attribute];
  const has = v !== undefined && v !== null && String(v).trim() !== '';
  const eq = () => has && String(v).trim().toLowerCase() === String(c.value ?? '').trim().toLowerCase();
  switch (String(c.op || 'EQUALS').trim().toUpperCase()) {
    case 'EXISTS': return has;
    case 'NOT_EQUALS': return has && !eq();
    case 'CONTAINS': return has && String(v).toLowerCase().includes(String(c.value ?? '').toLowerCase());
    default: return eq();
  }
}

/** A conditional field whose condition is unmet — the engine is ignoring it, so we must too. */
function isSuppressed(field, values) {
  return !!(field && field.requiredIf) && !isRequiredNow(field, values);
}

/**
 * Ready to quote. BOTH conditions, and this is not belt-and-braces.
 *
 * COMPLETE alone is not enough: verified against the engine, a session that had already completed
 * and then GAINS a requirement — the operator flipping the trip to a round trip — keeps
 * status COMPLETE while missingFields fills with the return leg, because the engine only downgrades
 * on the paths that re-ask. An empty gap list alone is not enough either, for the original reason
 * this flow is shaped around. Ready means both.
 */
export function isReady(turn) {
  return !!turn && turn.status === 'COMPLETE' && ((turn.missingFields || []).length === 0);
}

/**
 * Optional details we have not been told yet. These are NOT in missingFields and never block
 * completion — the engine does not ask them at all, by design (see the offerOptional note in the
 * flow README). They become a soft "if it helps" section so the customer is offered the chance to
 * answer without ever being required to. Null stays a legal answer.
 */
export function optionalGaps(turn) {
  const states = (turn && turn.attributeStates) || {};
  const missing = new Set((turn && turn.missingFields) || []);
  const values = cleanValues(turn);
  return INTAKE_FIELDS.filter((f) => (
    !isRequiredNow(f, values) && f.prompt && !missing.has(f.key) && states[f.key] === 'UNINITIALIZED'
    // A suppressed conditional must not be OFFERED either. Without this every one-way inquiry would
    // carry "What date does the group travel back?" in its if-you-know-these list, and every
    // inquiry would ask for a PO number nobody owes.
    && !isSuppressed(f, values)
  ));
}

/**
 * The APPROVER's channel — never the customer's. Everything here is for the Krapf reviewer:
 * non-blocking rule warnings, a return date that cannot be right, and any required field the engine
 * gave up on.
 */
export function reviewFlags(turn) {
  const flags = [];
  const values = cleanValues(turn);
  const states = (turn && turn.attributeStates) || {};
  const missing = new Set((turn && turn.missingFields) || []);

  Object.entries((turn && turn.warnings) || {}).forEach(([key, message]) => {
    flags.push({ kind: 'warning', key, message });
  });

  // Round trips are priced now, so there is no scope flag for one. What the ENGINE cannot check is
  // the relationship between two dates: a Rule is per-field (fieldKey + type + param) and has no way
  // to compare returnDate against departureDate, so the sanity check lives here. Reviewer-only —
  // it is far more likely to be a mis-parsed year than something to badger the customer about.
  if (values.returnDate && values.departureDate && values.returnDate < values.departureDate) {
    flags.push({
      kind: 'impossible',
      key: 'returnDate',
      message: `The return date (${values.returnDate}) is before the departure date (${values.departureDate}) — worth checking before this is priced.`,
    });
  }

  // An invalid OPTIONAL field never reaches the customer (see composeReply), so the reviewer is the
  // only one who will ever see it. Usually means the extractor put something odd in a choice field.
  Object.entries((turn && turn.validationErrors) || {}).forEach(([key, message]) => {
    const f = FIELD_BY_KEY[key];
    // Judged by what is required NOW: a round trip's bad return date is the customer's to fix and
    // belongs in their email, while the same field on a one-way trip is suppressed noise for us.
    if (f && !isRequiredNow(f, values)) {
      flags.push({
        kind: 'unusable',
        key,
        message: `Ignoring an unusable value for ${f.label}: ${message}`,
      });
    }
  });

  // onExhaustion: "skip" excuses a field the customer never answered. It then vanishes from
  // missingFields with no explanation, so the reviewer has to be told it was dropped.
  INTAKE_FIELDS.filter((f) => isRequiredNow(f, values)).forEach((f) => {
    if (states[f.key] === 'UNINITIALIZED' && !missing.has(f.key)) {
      flags.push({
        kind: 'skipped',
        key: f.key,
        message: `Asked repeatedly for ${f.label} and never got an answer — the intake moved on without it.`,
      });
    }
  });

  return flags;
}

/** Fields the customer changed on the most recent turn — the "change of plans" marker. */
export function changedKeys(turn) {
  return Object.entries((turn && turn.attributeStates) || {})
    .filter(([, state]) => state === 'MODIFIED')
    .map(([key]) => key);
}

export const THREAD_STATUS = {
  NOT_TRACKED: 'Not a trip inquiry',
  READY: 'Ready to quote',
  WAITING: 'Waiting on customer',
  NEEDS_REPLY: 'Needs reply',
  STALLED: 'Needs a human',
};

/**
 * The status chip for a thread. Readiness is isReady() — COMPLETE *and* no outstanding gaps; see
 * there for why each half is load-bearing.
 */
export function threadStatus(turn, { awaitingCustomer = false } = {}) {
  if (!turn) return THREAD_STATUS.NOT_TRACKED;
  if (isReady(turn)) return THREAD_STATUS.READY;
  if (turn.status === 'ABANDONED') return THREAD_STATUS.STALLED;
  return awaitingCustomer ? THREAD_STATUS.WAITING : THREAD_STATUS.NEEDS_REPLY;
}

/**
 * True when a turn told us nothing new — the reply carried no extractable detail and the gap list did
 * not move. Usually a pure question, or a message the off-topic guardrail swallowed (TurnResult
 * exposes no off-topic flag, so this is how we notice). Do NOT re-send the same email at a customer
 * who just asked a question; hand the thread to a human instead.
 */
export function turnWasUnproductive(turn, previousMissing) {
  const extractedNothing = Object.keys((turn && turn.extracted) || {}).length === 0;
  const before = (previousMissing || []).join('|');
  const after = ((turn && turn.missingFields) || []).join('|');
  return extractedNothing && before === after;
}

/**
 * Split an email body into plain and HIGHLIGHTED segments — the words the intake actually took its
 * values from, so a human can see what was read without re-reading the email themselves.
 *
 * Matching is VERBATIM and nothing cleverer, which is the honest limit of what TurnResult gives us:
 * `extracted` carries field -> final value, with no offsets back into the source text. So a value
 * only underlines when it appears literally — names, places, cities, emails, phone numbers usually
 * do. A value the extractor COMPUTED will not: "48 students and 6 staff" becomes passengers "54",
 * "Saturday, November 21" becomes an ISO date, and a derived state code was never in the email at
 * all. Those are silently skipped rather than guessed at, because a highlight on the wrong words is
 * worse than no highlight — it would tell the operator we read something we didn't.
 *
 * Longest values first so "Lakemont Ice Arena" wins over a bare "Lakemont" nested inside it, and
 * each region of the text is claimed once.
 */
export function highlightExtracted(body, turn) {
  const text = String(body || '');
  const extracted = (turn && turn.extracted) || {};
  const hay = text.toLowerCase();

  const spans = [];
  Object.entries(extracted)
    .filter(([key]) => !key.startsWith('__'))
    .map(([key, value]) => [key, String(value == null ? '' : value).trim()])
    // Two characters or fewer matches far too much to be meaningful ("PA" would light up "Park").
    .filter(([, value]) => value.length > 2)
    .sort((a, b) => b[1].length - a[1].length)
    .forEach(([key, value]) => {
      const needle = value.toLowerCase();
      let from = 0;
      for (;;) {
        const at = hay.indexOf(needle, from);
        if (at < 0) break;
        const end = at + needle.length;
        if (!spans.some((sp) => at < sp.end && end > sp.start)) {
          spans.push({ start: at, end, key, value });
        }
        from = at + 1;
      }
    });

  spans.sort((a, b) => a.start - b.start);

  const out = [];
  let cursor = 0;
  spans.forEach((sp) => {
    if (sp.start > cursor) out.push({ text: text.slice(cursor, sp.start) });
    out.push({ text: text.slice(sp.start, sp.end), key: sp.key });
    cursor = sp.end;
  });
  if (cursor < text.length) out.push({ text: text.slice(cursor) });
  return out;
}

/**
 * The reply split into lines, marking which are the NUMBERED required questions so the UI can weight
 * them. The plain text is unchanged — it is what gets sent and what the operator edits — this only
 * says which lines carry the asks.
 */
export function replyLines(body) {
  return String(body || '').split('\n').map((text) => ({ text, ask: /^\s*\d+\.\s/.test(text) }));
}

// ---- composing the reply --------------------------------------------------------------------

/**
 * The reply email body. One message, every outstanding question, numbered — the shape a charter
 * coordinator actually sends.
 *
 * Only two things may appear here: what the flow says is missing, and what failed validation. Review
 * flags are the approver's and never cross over.
 */
export function composeReply(turn, {
  customerName = '',
  senderName = '',
  companyName = 'Krapf Bus Company',
  senderPhone = '',
} = {}) {
  const greetingName = String(customerName || '').trim().split(/\s+/)[0];
  const lines = [greetingName ? `Hi ${greetingName},` : 'Hello,', ''];

  const missing = missingItems(turn);
  // Only a REQUIRED field's validation error may become a customer question. An invalid OPTIONAL
  // field is a reviewer concern: the engine's choice-mismatch message is written for a chat UI
  // ("we don't have 'field trip' — the available options are: one-way, round-trip") and reads as an
  // absurd demand in an email to someone who simply described their trip. Those go to reviewFlags.
  const invalid = Object.entries((turn && turn.validationErrors) || {})
    .filter(([key]) => isRequiredNow(FIELD_BY_KEY[key], cleanValues(turn)));
  const optional = optionalGaps(turn);

  if (isReady(turn)) {
    lines.push("Thanks — that's everything I need. I'm sending this over to be priced and will follow up with a quote shortly.");
  } else if (missing.length === 0 && invalid.length === 0) {
    lines.push("Thanks — I have what I need for now and I'm putting this together.");
  } else {
    lines.push('Happy to put this together. A few things I need before I can price it.');
    lines.push('');
    let n = 1;
    missing.forEach((item) => { lines.push(`${n}. ${item.prompt}`); n += 1; });
    invalid.forEach(([key, message]) => {
      const label = (FIELD_BY_KEY[key] && FIELD_BY_KEY[key].label) || key;
      lines.push(`${n}. ${message || `Could you double-check the ${label}?`}`);
      n += 1;
    });
    lines.push('');
    lines.push("Once I have those, I'll get this priced for you.");
  }

  // Soft, explicitly optional, and always last so it can never read as a requirement.
  if (optional.length) {
    lines.push('');
    lines.push("If you already know these, they help us plan — but don't hold up your reply for them:");
    optional.forEach((f) => lines.push(`  • ${f.prompt}`));
  }

  lines.push('');
  lines.push('Thanks,');
  if (senderName) lines.push(senderName);
  lines.push(companyName);
  if (senderPhone) lines.push(senderPhone);

  return lines.join('\n');
}

export { replySubject };
